"use client"

import { useEffect } from "react"

interface Run {
  animation: Animation
  opening: boolean
  name: string | null
}

const runs = new WeakMap<HTMLDetailsElement, Run>()

export const token = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim()

export const milliseconds = (time: string) => {
  const value = parseFloat(time)
  if (Number.isNaN(value)) return undefined
  return time.endsWith("ms") ? value : time.endsWith("s") ? value * 1000 : undefined
}

const heightOf = (details: HTMLDetailsElement, open: boolean) => {
  details.open = open
  return details.getBoundingClientRect().height
}

const finish = (details: HTMLDetailsElement, run: Run) => {
  runs.delete(details)
  details.style.overflow = ""
  details.removeAttribute("data-closing")
  if (!run.opening) details.open = false
  if (run.name !== null) details.setAttribute("name", run.name)
}

const settleDisclosure = (details: HTMLDetailsElement, opening: boolean) => {
  const previous = runs.get(details)
  const from = details.getBoundingClientRect().height
  previous?.animation.cancel()
  const name = previous ? previous.name : details.getAttribute("name")
  details.removeAttribute("name")
  const openHeight = heightOf(details, true)
  const closedHeight = heightOf(details, false)
  details.open = true
  details.toggleAttribute("data-closing", !opening)
  details.style.overflow = "clip"
  const animation = details.animate(
    { height: [`${from}px`, `${opening ? openHeight : closedHeight}px`] },
    { duration: milliseconds(token("--duration-moderate")) ?? 240, easing: token("--ease-settle") || "ease-out" }
  )
  const run = { animation, opening, name }
  runs.set(details, run)
  animation.onfinish = () => finish(details, run)
}

const isOpening = (details: HTMLDetailsElement) => {
  const run = runs.get(details)
  return run ? !run.opening : !details.open
}

export const onSummaryClick = (event: MouseEvent) => {
  if (!(event.target instanceof Element)) return
  const summary = event.target.closest("summary")
  const details = summary?.parentElement
  if (!(details instanceof HTMLDetailsElement) || !details.classList.contains("disclosure") || details.querySelector(":scope > summary") !== summary) return
  if (typeof details.animate !== "function" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
  event.preventDefault()
  const opening = isOpening(details)
  const group = runs.get(details)?.name ?? details.getAttribute("name")
  if (opening && group) {
    for (const other of document.querySelectorAll<HTMLDetailsElement>("details.disclosure")) {
      if (other === details || isOpening(other)) continue
      const otherGroup = runs.get(other)?.name ?? other.getAttribute("name")
      if (otherGroup === group) settleDisclosure(other, false)
    }
  }
  settleDisclosure(details, opening)
}

export const DisclosureMotion = () => {
  useEffect(() => {
    document.documentElement.setAttribute("data-disclosure-motion", "")
    document.addEventListener("click", onSummaryClick)
    return () => {
      document.removeEventListener("click", onSummaryClick)
      document.documentElement.removeAttribute("data-disclosure-motion")
    }
  }, [])

  return null
}
